import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Space, Table, Tag, Card, Popconfirm } from 'antd';
import { CopyOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

import { setAppTitle } from 'src/store/app';
import { giveawayApi } from 'src/api';

import {
  fetchGiveaways,
  fetchParticipiants,
  giveawaysListSelector,
  giveawaysMetaDataSelector,
  isLoadingGivewaySelector,
  isPickingWinnerSelector,
  isRerollingWinnerSelector,
  isLoadingParticipiantsSelector,
  pickWinner,
  rerollWinner,
  participiantsListSelector,
  participiantsMetaSelector
} from 'src/store/giveaway';

import { fetchCommands, commandsListSelector } from 'src/store/command';

import { GiveawayModal } from './Modal/Modal';

import './giveaway.scss';

export const Giveaway = () => {
  const { t } = useTranslation('giveaway');
  const dispatch = useDispatch();

  const giveaways = useSelector(giveawaysListSelector);
  const giveawaysMeta = useSelector(giveawaysMetaDataSelector);
  const isLoading = useSelector(isLoadingGivewaySelector);

  const participants = useSelector(participiantsListSelector);
  const participantsMeta = useSelector(participiantsMetaSelector);
  const isLoadingParticipiants = useSelector(isLoadingParticipiantsSelector);
  const isPickingWinner = useSelector(isPickingWinnerSelector);
  const isRerollingWinner = useSelector(isRerollingWinnerSelector);

  const commands = useSelector(commandsListSelector);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [winnerModalOpen, setWinnerModalOpen] = useState(false);
  const [pickedWinners, setPickedWinners] = useState([]);
  const [participantsGiveawayId, setParticipantsGiveawayId] = useState(null);
  const [isCopyingParticipants, setIsCopyingParticipants] = useState(false);
  const [giveawaysPagination, setGiveawaysPagination] = useState({ page: 1, perPage: 10 });
  const [participantsPagination, setParticipantsPagination] = useState({ page: 1, perPage: 10 });

  const activeGiveaway = giveaways.find((item) => item.status === 'active');

  useEffect(() => {
    dispatch(setAppTitle(t('menu:giveaway')));
  }, [dispatch, t]);

  useEffect(() => {
    dispatch(fetchGiveaways(giveawaysPagination));
  }, [dispatch, giveawaysPagination]);

  useEffect(() => {
    dispatch(fetchCommands());
  }, [dispatch]);

  useEffect(() => {
    if (activeGiveaway?._id) {
      setParticipantsGiveawayId(activeGiveaway._id);
      setParticipantsPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [activeGiveaway]);

  useEffect(() => {
    if (!participantsGiveawayId && giveaways?.[0]?._id) {
      setParticipantsGiveawayId(giveaways[0]._id);
    }
  }, [giveaways, participantsGiveawayId]);

  useEffect(() => {
    if (participantsGiveawayId) {
      dispatch(
        fetchParticipiants({
          giveawayId: participantsGiveawayId,
          page: participantsPagination.page,
          perPage: participantsPagination.perPage
        })
      );
    }
  }, [dispatch, participantsGiveawayId, participantsPagination]);

  const ticketCommands = useMemo(() => {
    return commands.filter((item) => item?.isGiveawayCommand).filter((item) => item?.active);
  }, [commands]);

  const openCreateModal = useCallback(() => {
    setSelectedGiveaway(null);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback(
    (item) => () => {
      setSelectedGiveaway(item);
      setIsModalOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedGiveaway(null);
  }, []);

  const handlePickWinner = useCallback(async () => {
    if (!activeGiveaway?._id) return;

    setParticipantsGiveawayId(activeGiveaway._id);

    const response = await dispatch(pickWinner({ giveawayId: activeGiveaway._id }));

    const winners = response?.payload?.data || [];

    if (!response?.error) {
      setPickedWinners(winners);
      setWinnerModalOpen(true);
    }
  }, [activeGiveaway, dispatch]);

  const handleGiveawaysPageChange = useCallback((pagination) => {
    setGiveawaysPagination({
      page: pagination?.current || 1,
      perPage: pagination?.pageSize || 10
    });
  }, []);

  const handleParticipantsPageChange = useCallback((pagination) => {
    setParticipantsPagination({
      page: pagination?.current || 1,
      perPage: pagination?.pageSize || 10
    });
  }, []);

  const handleRerollWinner = useCallback(
    async (giveaway, winner) => {
      const giveawayId = giveaway?._id;
      const entryId = winner?.entryId?._id;

      if (!giveawayId || !entryId) return;

      setParticipantsGiveawayId(giveawayId);

      const response = await dispatch(rerollWinner({ giveawayId, entryId }));
      const winners = response?.payload?.data || [];

      if (!response?.error) {
        setPickedWinners(winners);
        setWinnerModalOpen(true);
      }
    },
    [dispatch]
  );

  const handleCopyParticipants = useCallback(async () => {
    if (!participantsGiveawayId) return;

    const totalParticipants = participantsMeta?.total || participants.length || 0;

    if (!totalParticipants) {
      toast.info(t('copy.no_participants', 'There are no participants to copy yet.'));
      return;
    }

    setIsCopyingParticipants(true);

    try {
      const response = await giveawayApi.fetchParticipiants({
        giveawayId: participantsGiveawayId,
        page: 1,
        perPage: totalParticipants
      });

      const allParticipants = response?.data?.data || [];

      const text = allParticipants
        .map((item) => {
          const ticketNumber = item?.ticketNumber ?? '-';
          const userName = item?.nickname || item?.viewerId?.login || 'Unknown';

          return `${ticketNumber} - ${userName}`;
        })
        .filter(Boolean)
        .join('\n');

      await navigator.clipboard.writeText(text);

      toast.success(t('copy.participants_copied', { count: allParticipants.length }));
    } catch (error) {
      console.error('copyParticipants[error]', error);
      toast.error(t('copy.copy_failed', 'Failed to copy participants'));
    } finally {
      setIsCopyingParticipants(false);
    }
  }, [participantsGiveawayId, participantsMeta?.total, participants.length, t]);

  const giveawayColumns = useMemo(() => {
    return [
      {
        title: t('table.title'),
        dataIndex: 'title',
        key: 'title'
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (status) => {
          const isActive = status === 'active';
          return <Tag color={isActive ? 'green' : 'default'}>{status}</Tag>;
        }
      },
      {
        title: t('fields.winners'),
        dataIndex: 'winnersCount',
        key: 'winnersCount',
        width: 150,
        render: (value) => value ?? 1
      },
      {
        title: t('table.participants'),
        dataIndex: 'participantsCount',
        key: 'participantsCount',
        width: 140,
        render: (value) => value ?? 0
      },
      {
        title: t('table.winners'),
        dataIndex: 'winners',
        key: 'winners',
        render: (_, row) => {
          const winners = row?.winners || [];
          if (!winners.length) return <span>-</span>;

          return (
            <Space direction="vertical" size="small">
              {winners.map((winner, index) => {
                const label =
                  winner?.entryId?.nickname || winner?.viewerId?.login || winner?.nickname || '';
                const entryId = winner?.entryId?._id;
                const ticketNumber = winner?.entryId?.ticketNumber ?? winner?.ticketNumber;

                if (!label) return null;

                return (
                  <Space size="small" wrap key={`${label}-${index}`}>
                    <Tag color="gold">
                      {Number.isFinite(Number(ticketNumber))
                        ? `${label} #${Number(ticketNumber)}`
                        : label}
                    </Tag>

                    {row?.status === 'finished' && entryId ? (
                      <Popconfirm
                        title={t(
                          'winner_modal.reroll_confirm',
                          'Replace this winner with a new random participant?'
                        )}
                        onConfirm={() => handleRerollWinner(row, winner)}
                        okButtonProps={{ loading: isRerollingWinner }}
                      >
                        <Button size="small" loading={isRerollingWinner}>
                          {t('actions.reroll', 'Reroll')}
                        </Button>
                      </Popconfirm>
                    ) : null}
                  </Space>
                );
              })}
            </Space>
          );
        }
      },
      {
        title: t('table.ticket_command'),
        dataIndex: 'commandSnapshot',
        key: 'commandSnapshot',
        render: (snapshot, row) => {
          const trigger = snapshot?.trigger || '';
          if (trigger) return <Tag color="purple">{trigger}</Tag>;

          const command = ticketCommands.find((item) => item?._id === row?.ticketCommandId);

          return command?.trigger ? <Tag color="purple">{command.trigger}</Tag> : <span>—</span>;
        }
      },
      {
        title: t('table.actions'),
        key: 'actions',
        width: 120,
        render: (_, item) => (
          <Space size="small">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={openEditModal(item)}
              disabled={item?.status === 'finished'}
            >
              {t('actions:edit')}
            </Button>
          </Space>
        )
      }
    ];
  }, [handleRerollWinner, isRerollingWinner, openEditModal, t, ticketCommands]);

  const participationColumns = useMemo(() => {
    return [
      {
        title: t('table.nickname'),
        dataIndex: 'nickname',
        key: 'nickname'
      },
      {
        title: t('table.ticketNumber'),
        dataIndex: 'ticketNumber',
        key: 'ticketNumber'
      },
      {
        title: t('table.viewerId'),
        dataIndex: 'viewerId',
        key: 'viewerId'
      }
    ];
  }, [t]);

  return (
    <div className="giveaway">
      <div className="giveaway__content">
        <div className="giveaway__header">
          <div />
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            {t('actions.create')}
          </Button>
        </div>

        <Card className="giveaway__card" bordered={false}>
          <Table
            rowKey={(row) => row?._id}
            columns={giveawayColumns}
            dataSource={giveaways}
            loading={isLoading}
            onChange={handleGiveawaysPageChange}
            pagination={{
              current: giveawaysMeta?.page || giveawaysPagination.page,
              pageSize: giveawaysMeta?.perPage || giveawaysPagination.perPage,
              total: giveawaysMeta?.total || 0,
              showSizeChanger: true
            }}
          />
        </Card>

        <div className="giveaway__header giveaway__header--second">
          <div />
          <Space>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyParticipants}
              loading={isCopyingParticipants}
              disabled={!participantsGiveawayId}
            >
              {t('actions.copy_participants')}
            </Button>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handlePickWinner}
              loading={isPickingWinner}
              disabled={!activeGiveaway}
            >
              {t('actions.choose')}
            </Button>
          </Space>
        </div>

        <Card className="giveaway__card" bordered={false}>
          <Table
            rowKey={(row) => row?._id}
            columns={participationColumns}
            dataSource={participants}
            loading={isLoadingParticipiants}
            onChange={handleParticipantsPageChange}
            pagination={{
              current: participantsMeta?.page || participantsPagination.page,
              pageSize: participantsMeta?.perPage || participantsPagination.perPage,
              total: participantsMeta?.total || 0,
              showSizeChanger: true
            }}
          />
        </Card>
      </div>

      <GiveawayModal
        isModalOpen={isModalOpen}
        selectedGiveaway={selectedGiveaway}
        onCancel={closeModal}
        ticketCommands={ticketCommands}
      />

      <Modal
        title={t('winner_modal.title', 'Winner selected')}
        open={winnerModalOpen}
        onCancel={() => setWinnerModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setWinnerModalOpen(false)}>
            {t('actions:cancel')}
          </Button>
        ]}
      >
        {pickedWinners.length ? (
          <div>
            {pickedWinners.map((item, index) => {
              const nickname = item?.nickname;
              const ticketNumber = item?.ticketNumber ?? '-';

              return (
                <div key={item?._id}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {pickedWinners.length > 1
                      ? t('winner_modal.item_title', 'Winner') + ` #${index + 1}`
                      : t('winner_modal.item_title', 'Winner / Reroll')}
                  </div>
                  <div>
                    {t('winner_modal.nickname_label', 'Nickname')}: {nickname}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    {t('winner_modal.ticket_label', 'Ticket Number')}: {ticketNumber}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>{t('winner_modal.no_data', 'Winner selected.')}</div>
        )}
      </Modal>
    </div>
  );
};
